<!--
  CompanyAccount-Indemnitor-Progress.vue

  Live progress panel for the staff portal's indemnitor packet view.
  Renders the schema as a section list overlaid with the packet's
  field_states, showing staff which sections the indemnitor has
  finished, which is currently being worked, and which are still
  untouched. Updates ~live via SSE because the parent component
  refetches detail (which then re-flows props into this component)
  whenever the packet doc changes in Mongo.

  Schema-driven and customer-agnostic: any customer's schema, plus
  the packet that goes with it, plugs in unchanged. The rules for
  "what counts" live in three places only:
    - filled_by ∈ {indemnitor, either}: indemnitor-facing fields
    - condition: hidden-by-condition fields don't count toward total
    - state ∈ {filled, skipped}: counts as done

  Sensitive fields (schema flag) are not displayed by value — only
  state. Keeps PII out of the panel even though staff are technically
  authorized to see it (it's in the PDF/email flow). Reduces glance
  exposure.

  Auto-expand: on mount (and when the packet ID changes), the section
  containing the most recently touched field is auto-expanded; the
  rest are collapsed. Manual toggles after that are user-controlled
  and not overwritten by reactive recomputation, so the panel
  doesn't snap around as the indemnitor types into a different
  section.
-->
<template>
  <div class="progress-panel">
    <!--
      Header is a button so it's keyboard-accessible and the entire bar
      is the click target (not just the chevron). Default state is
      furled: the row-header glance indicator on the list view already
      gives staff a progress signal at a glance, so the full panel
      stays out of the way until staff opt in.
    -->
    <button
      type="button"
      class="progress-header"
      @click="togglePanel"
      :aria-expanded="panelOpen"
    >
      <span class="progress-header-summary">
        <v-icon class="progress-chevron" size="18">
          {{ panelOpen ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
        </v-icon>
        <strong>{{ overall.sectionsComplete }} of {{ overall.sectionsTotal }}</strong>
        <span class="progress-summary-label">sections complete</span>
        <span class="progress-summary-fields">
          ({{ overall.filled }}/{{ overall.total }} fields)
        </span>
      </span>
      <span v-if="lastTouched" class="last-touched" :title="lastTouchedTitle">
        Last update {{ lastTouchedRelative }}:
        <span class="last-touched-field">{{ lastTouched.label }}</span>
      </span>
    </button>

    <v-expand-transition>
      <div v-show="panelOpen" class="section-list">
        <div
          v-for="section in sectionsData"
          :key="section.name"
          class="section-row"
          :class="{
            'section-complete': section.isComplete,
            'section-active': section.isActive,
          }"
        >
          <button
            type="button"
            class="section-header"
            @click="toggleSection(section.name)"
            :aria-expanded="isExpanded(section.name)"
          >
            <v-icon
              class="section-chevron"
              size="16"
            >{{ isExpanded(section.name) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>

            <span class="section-name">{{ section.name }}</span>

            <span class="section-progress">
              <span class="section-counts">
                {{ section.filledCount + section.skippedCount }}/{{ section.totalCount }}
              </span>
              <v-progress-linear
                :model-value="(section.filledCount + section.skippedCount) / section.totalCount * 100"
                height="4"
                :color="section.isComplete ? 'success' : (section.isActive ? 'primary' : 'grey-lighten-1')"
                class="section-bar"
                rounded
              />
              <v-icon
                v-if="section.isComplete"
                class="section-done-check"
                size="14"
                color="success"
              >mdi-check-circle</v-icon>
            </span>
          </button>

          <div v-if="isExpanded(section.name)" class="section-fields">
            <div
              v-for="field in section.fields"
              :key="field.name"
              class="field-row"
              :class="'field-state-' + field.state"
            >
              <span class="field-state-icon">
                <v-icon v-if="field.state === 'filled'"   size="14" color="success">mdi-check</v-icon>
                <v-icon v-else-if="field.state === 'skipped'" size="14" color="grey-darken-1">mdi-minus-circle-outline</v-icon>
                <v-icon v-else                              size="14" color="grey-lighten-1">mdi-circle-small</v-icon>
              </span>

              <span class="field-label">{{ field.label }}</span>

              <template v-if="field.displayValue !== null">
                <v-icon class="field-value-arrow" size="12">mdi-arrow-right-thin</v-icon>
                <span class="field-value-text">{{ field.displayValue }}</span>
              </template>

              <span v-if="field.sensitive" class="field-sensitive-badge" title="Sensitive — value masked">
                <v-icon size="12">mdi-lock-outline</v-icon>
              </span>

              <span v-if="field.state === 'skipped'" class="field-state-detail">
                skipped<span v-if="field.skipReason"> — {{ field.skipReason }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </v-expand-transition>
  </div>
</template>


<script>
import moment from 'moment-timezone';

export default {
  name: 'CompanyAccountIndemnitorProgress',

  props: {
    // The full schema as returned by /indemnitor/entry/staff/schema.
    // Read-only here; the parent owns the fetch and caching.
    schema: { type: Object, required: true },

    // The detail-endpoint packet shape: includes field_states, and
    // the bond / indemnitor sub-objects with sensitive fields already
    // decrypted server-side (getPacketDetail does that pass).
    packet: { type: Object, required: true },
  },

  data() {
    return {
      // Panel furl/unfurl. Default furled — the row-header glance
      // indicator on the list view already gives the at-a-glance
      // signal, so the full panel stays out of the way until the
      // user opts in. Reset to false whenever the user opens a
      // different packet (see watcher).
      panelOpen: false,

      // Manual toggle state — keyed by section name. We seed this on
      // first render (and when the packet ID changes) using
      // _seedAutoExpand(), then leave user toggles alone.
      // Storing as an object (not Set) so Vue 3 reactivity tracks it.
      expandedSections: {},

      // Tracks which packet's defaults we last seeded, so we know to
      // re-seed when the user navigates to a different packet but
      // not on every reactive prop tick.
      seededForPacketId: null,
    };
  },

  computed: {
    // ---- Derived data ----------------------------------------------

    fieldStates() {
      return this.packet?.field_states || {};
    },

    sensitiveFieldNames() {
      // Walk the schema for the per-field sensitive flag. Cached as
      // a computed so subsequent accesses (e.g., per-field renders)
      // don't re-walk.
      const names = new Set();
      for (const section of this.schema?.sections || []) {
        for (const field of section.fields || []) {
          if (field.sensitive === true) names.add(field.name);
        }
      }
      return names;
    },

    // The values map used by condition evaluation — mirrors the
    // backend's _evaluateCondition signature: { bond, indemnitor }.
    conditionValues() {
      return {
        bond: this.packet?.bond || {},
        indemnitor: this.packet?.indemnitor || {},
      };
    },

    // The big one. Walks every section, filters to indemnitor-facing
    // fields whose conditions evaluate true, attaches state, and
    // computes section-level totals. Sections that end up with no
    // visible fields (e.g. all-system sections) are filtered out
    // entirely — showing "0/0" for them is noise.
    sectionsData() {
      const out = [];
      const activeName = this._activeSectionName;

      for (const section of this.schema?.sections || []) {
        const fields = [];
        let filledCount = 0;
        let skippedCount = 0;

        for (const field of section.fields || []) {
          if (field.filled_by !== 'indemnitor' && field.filled_by !== 'either') continue;
          if (!this._evaluateCondition(field.condition, this.conditionValues)) continue;

          const fs = this.fieldStates[field.name] || {};
          const state = fs.state || 'unreached';
          if (state === 'filled')  filledCount += 1;
          if (state === 'skipped') skippedCount += 1;

          // Compute displayValue only for filled fields. Skipped/unreached
          // have nothing to show. For sensitive fields we format first
          // (so the formatter sees the right type), then mask — so e.g.
          // an SSN renders as •••-••-6789 with separators preserved.
          let displayValue = null;
          if (state === 'filled') {
            const raw = this._lookupValue(field.name);
            const formatted = this._formatValue(field, raw);
            displayValue = field.sensitive
              ? this._maskValue(formatted)
              : formatted;
          }

          fields.push({
            name: field.name,
            label: this._displayLabel(field),
            state,
            sensitive: field.sensitive === true,
            updatedAt: fs.updated_at || null,
            skipReason: fs.reason || null,
            displayValue,
          });
        }

        if (fields.length === 0) continue;  // no visible indemnitor fields

        const totalCount = fields.length;
        const isComplete = (filledCount + skippedCount) === totalCount;

        out.push({
          name: section.section || '(unnamed section)',
          fields,
          filledCount,
          skippedCount,
          totalCount,
          isComplete,
          isActive: section.section === activeName,
        });
      }

      return out;
    },

    // The section that contains the most-recently-touched field,
    // determined by the latest field_states[*].updated_at across all
    // visible indemnitor-facing fields. Used to auto-expand the
    // section the indemnitor is currently in.
    _activeSectionName() {
      let bestAt = null;
      let bestSection = null;

      for (const section of this.schema?.sections || []) {
        for (const field of section.fields || []) {
          if (field.filled_by !== 'indemnitor' && field.filled_by !== 'either') continue;
          const fs = this.fieldStates[field.name];
          if (!fs?.updated_at) continue;
          const t = new Date(fs.updated_at).getTime();
          if (bestAt === null || t > bestAt) {
            bestAt = t;
            bestSection = section.section || null;
          }
        }
      }
      return bestSection;
    },

    // The latest touched field overall, for the header indicator.
    // Returns { name, label, sectionName, updatedAt } or null.
    lastTouched() {
      let best = null;

      for (const section of this.schema?.sections || []) {
        for (const field of section.fields || []) {
          if (field.filled_by !== 'indemnitor' && field.filled_by !== 'either') continue;
          const fs = this.fieldStates[field.name];
          if (!fs?.updated_at) continue;
          const t = new Date(fs.updated_at).getTime();
          if (!best || t > best._t) {
            best = {
              name: field.name,
              label: this._displayLabel(field),
              sectionName: section.section,
              updatedAt: fs.updated_at,
              _t: t,
            };
          }
        }
      }
      return best;
    },

    lastTouchedRelative() {
      if (!this.lastTouched) return '';
      return moment(this.lastTouched.updatedAt).fromNow();
    },

    lastTouchedTitle() {
      if (!this.lastTouched) return '';
      const abs = moment(this.lastTouched.updatedAt).format('YYYY-MM-DD HH:mm:ss z');
      return `${abs} — ${this.lastTouched.sectionName || ''}`;
    },

    // Roll-up totals for the panel header.
    overall() {
      let filled = 0;
      let total = 0;
      let sectionsComplete = 0;
      const sectionsTotal = this.sectionsData.length;

      for (const sec of this.sectionsData) {
        // total here counts EVERY visible indemnitor field, not just
        // unfinished ones. The header's "X/Y fields" is meant to be
        // a global progress signal independent of section grouping.
        total += sec.totalCount;
        filled += sec.filledCount + sec.skippedCount;
        if (sec.isComplete) sectionsComplete += 1;
      }

      return { filled, total, sectionsComplete, sectionsTotal };
    },
  },

  watch: {
    // Re-seed auto-expand defaults when the user opens a different
    // packet (parent passes a new packet object). Within the same
    // packet, we deliberately do NOT re-seed on every prop tick —
    // SSE updates would otherwise yank sections open/closed under
    // the user's mouse.
    'packet._id': {
      immediate: true,
      handler(newId) {
        if (newId && newId !== this.seededForPacketId) {
          // Reset to default-furled on every new packet open. The
          // per-section auto-expand still gets seeded so when the
          // user unfurls, the active section is already open.
          this.panelOpen = false;
          this._seedAutoExpand();
          this.seededForPacketId = newId;
        }
      },
    },
  },

  methods: {
    // ---- Expansion control ----------------------------------------

    togglePanel() {
      this.panelOpen = !this.panelOpen;
    },

    isExpanded(sectionName) {
      return this.expandedSections[sectionName] === true;
    },

    toggleSection(sectionName) {
      // Vue 3: replace the object so reactivity fires reliably even
      // for keys not yet present in the original seed.
      this.expandedSections = {
        ...this.expandedSections,
        [sectionName]: !this.expandedSections[sectionName],
      };
    },

    _seedAutoExpand() {
      // Active section open, everything else closed. Sections with
      // zero visible fields don't appear in sectionsData and so
      // aren't keyed here — they'll never render.
      const seed = {};
      const active = this._activeSectionName;
      for (const section of this.schema?.sections || []) {
        seed[section.section] = (section.section === active);
      }
      this.expandedSections = seed;
    },

    // ---- Schema helpers -------------------------------------------

    _displayLabel(field) {
      // Staff portal is English. Fall back through display_label.en-US,
      // top-level label, then the field name as a last resort.
      return field.display_label?.['en-US']
          || field.label
          || field.name;
    },

    // Port of the backend's _evaluateCondition. Kept in lockstep so
    // the staff progress panel and server-side sections_progress
    // never disagree on whether a field is currently visible.
    // Reference: Indemnitor.js _evaluateCondition.
    _evaluateCondition(condition, values) {
      if (!condition) return true;

      const valOf = (name) => {
        if (values.bond && name in values.bond)               return values.bond[name];
        if (values.indemnitor && name in values.indemnitor)   return values.indemnitor[name];
        if (name in values)                                   return values[name];
        return undefined;
      };

      const op = condition.operator;

      if (op === 'equals')     return valOf(condition.field) === condition.value;
      if (op === 'not_equals') return valOf(condition.field) !== condition.value;
      if (op === 'truthy') {
        const v = valOf(condition.field);
        return !!v && v !== '';
      }
      if (op === 'any_of') {
        return (condition.fields || []).some((f) => {
          const v = valOf(f);
          return !!v && v !== '';
        });
      }
      if (op === 'all_of') {
        return (condition.conditions || []).every((c) => this._evaluateCondition(c, values));
      }
      return true;  // unknown operator — fail open, matches backend
    },

    // ---- Value display ---------------------------------------------

    // Look up a field's value from the packet's bond/indemnitor sub-objects.
    // Mirrors the valOf() inside _evaluateCondition so display values come
    // from the same source the condition logic does. Returns the raw value
    // (whatever shape the server stored) or undefined.
    _lookupValue(fieldName) {
      const bond = this.packet?.bond;
      const indemnitor = this.packet?.indemnitor;
      if (bond && fieldName in bond) return bond[fieldName];
      if (indemnitor && fieldName in indemnitor) return indemnitor[fieldName];
      return undefined;
    },

    // Format a raw value for display per its schema type. Returns a
    // string, or null when there's nothing useful to show. Storage
    // conventions (per schema meta.naming_conventions): SSN is digits-
    // only, phones are E.164, dates are ISO YYYY-MM-DD, currency is a
    // number.
    _formatValue(field, raw) {
      if (raw === undefined || raw === null || raw === '') return null;

      switch (field.type) {
        case 'dob':
        case 'date': {
          const m = moment(raw, 'YYYY-MM-DD', true);
          return m.isValid() ? m.format('MMM D, YYYY') : String(raw);
        }
        case 'ssn': {
          const digits = String(raw).replace(/\D/g, '');
          if (digits.length === 9) {
            return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
          }
          return digits || String(raw);
        }
        case 'phone': {
          const digits = String(raw).replace(/\D/g, '');
          // Strip US country-code 1 if present, then format (xxx) xxx-xxxx.
          // Foreign numbers fall through to a digit-only display.
          const local = digits.length === 11 && digits.startsWith('1')
            ? digits.slice(1)
            : digits;
          if (local.length === 10) {
            return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
          }
          return String(raw);
        }
        case 'currency': {
          const n = Number(raw);
          if (!Number.isFinite(n)) return String(raw);
          return n.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
          });
        }
        case 'boolean':
          return (raw === true || raw === 'true') ? 'Yes' : 'No';
        default:
          return String(raw);
      }
    },

    // Mask a formatted value for sensitive fields. Uniform last-4
    // reveal: replace every char except the trailing 4 with a bullet.
    // Short values (≤5 chars) get fully masked so we don't leak nearly
    // the whole thing on edge cases. Note this masks separators too —
    // a formatted SSN renders as •••••••6789, not •••-••-6789. If we
    // want separator-preserving masks later, add a second helper; for
    // now, simple wins.
    _maskValue(formatted) {
      if (formatted === null) return null;
      return formatted.length > 5
        ? '•'.repeat(formatted.length - 4) + formatted.slice(-4)
        : '•'.repeat(formatted.length);
    },
  },
};
</script>


<style scoped>
.progress-panel {
  background-color: #fff;
  border: 1px solid #e0d5c0;
  border-radius: 4px;
  padding: 12px 16px;
}

/* Header is a <button> for accessibility; reset the default chrome
   so it looks like a clickable bar, not an OS button. The whole bar
   is the click target. */
.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  width: 100%;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: #555;
  font-size: 13px;
  flex-wrap: wrap;
}
.progress-header:hover {
  color: #333;
}
.progress-header:focus-visible {
  outline: 2px solid #d2b48c;
  outline-offset: 2px;
  border-radius: 2px;
}

.progress-header-summary {
  display: flex;
  align-items: center;
  gap: 6px;
}

.progress-chevron {
  color: #999;
  flex-shrink: 0;
}
.progress-header:hover .progress-chevron {
  color: #555;
}

.progress-summary-label {
  margin-left: 0;
}
.progress-summary-fields {
  margin-left: 4px;
  color: #999;
  font-size: 12px;
}
.progress-header strong {
  color: #333;
  font-size: 14px;
}

.last-touched {
  font-size: 12px;
  color: #888;
}
.last-touched-field {
  color: #555;
  font-weight: 500;
}

/* Section list — gets a small top margin only when visible to
   separate from the header without leaving an awkward gap when
   collapsed. The v-expand-transition collapses the whole element
   including margin, so it's safe to specify here. */
.section-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 10px;
}

/* Speed up Vuetify's expand transition. Default is ~300ms; "real
   quick" is closer to 150ms. Targets the v-expand-transition
   classes Vuetify emits at runtime. */
:deep(.v-expand-transition-enter-active),
:deep(.v-expand-transition-leave-active) {
  transition-duration: 150ms !important;
}

.section-row {
  border-bottom: 1px solid #f0e8d8;
}
.section-row:last-child {
  border-bottom: none;
}

.section-header {
  display: grid;
  grid-template-columns: 16px 1fr auto;
  gap: 8px;
  align-items: center;
  width: 100%;
  background: transparent;
  border: 0;
  padding: 6px 0;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
}
.section-header:hover {
  background-color: #faf6ec;
}

.section-chevron {
  color: #999;
}

.section-name {
  font-size: 13px;
  color: #444;
}
.section-active .section-name {
  font-weight: 600;
  color: #222;
}
.section-complete .section-name {
  color: #666;
}

.section-progress {
  display: grid;
  grid-template-columns: auto 100px auto;
  gap: 8px;
  align-items: center;
}

.section-counts {
  font-size: 12px;
  color: #777;
  font-variant-numeric: tabular-nums;
  min-width: 36px;
  text-align: right;
}

.section-bar {
  width: 100px;
}

.section-done-check {
  width: 14px;
}

/* Field rows under an expanded section */
.section-fields {
  padding: 4px 0 8px 24px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.field-row {
  /* Flex (not grid) so the value sits inline right after the label
     instead of being pushed to a right-aligned trailing column. The
     icon stays in a fixed-width slot to keep labels vertically aligned
     across rows even when some rows have values and others don't. */
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  padding: 2px 0;
}

.field-state-icon {
  flex: 0 0 16px;
  display: inline-flex;
  justify-content: center;
}

.field-label {
  color: #555;
}
.field-state-filled .field-label {
  color: #333;
}
.field-state-unreached .field-label {
  color: #999;
}

.field-sensitive-badge {
  color: #999;
  display: inline-flex;
}

.field-state-detail {
  color: #888;
  font-size: 11.5px;
  font-style: italic;
}

/* Value display next to a filled field. Arrow is the success green so
   it visually ties to the green check on the left, and the value text
   itself is monospaced-numeric (not italic) since it's data, not
   commentary. The state-detail column already has text-align: right,
   which puts the arrow+value group at the row's trailing edge. */
.field-value-arrow {
  color: #4caf50;
  opacity: 0.7;
  margin-right: 2px;
  vertical-align: -2px;
}
.field-value-text {
  color: #333;
  font-style: normal;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}
</style>
